import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { TabsLine } from '@components/ui/TabsLine';
import { AbakoLogo } from '@/components/ui/AbakoLogo';

interface MainScreenProps {
  /** Ruta a la que ir al pulsar el botón de volver. Si no se envía, se oculta la barra superior. */
  backRoute?: string;
  /** Texto opcional para el botón de volver. Por defecto 'Back'. */
  backTitle?: string;
  /** Componente opcional que se renderizará en el header. */
  headerContent?: ReactNode;
  /** Array de tabs a mostrar (el componente TabsLine) */
  tabs?: { id: string; label: string }[];
  /** ID del tab activo actualmente. */
  activeTab?: string;
  /** Función que se ejecuta al cambiar de tab. */
  onTabChange?: (tabId: string) => void;
  /** Clase opcional para el contenedor que envuelve el contenido principal. */
  contentClassName?: string;
  /** Contenido principal de la página. */
  children: ReactNode;
}

export function MainScreen({
  backRoute,
  backTitle,
  headerContent,
  tabs,
  activeTab,
  onTabChange,
  contentClassName,
  children,
}: MainScreenProps) {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col w-full h-full bg-[var(--base-surface-1,#141414)]">
      {/* 1. Cabecera pequeña con botón de retroceso */}
      {backRoute && (
        <div className="w-full h-fit shrink-0 px-[--spacing-17] py-[--spacing-8] flex justify-between items-center px-4 bg-[--base-surface-1] border-b border-[var(--base-border,#3d3d3d)]">
          <button
            onClick={() => navigate(backRoute)}
            className="flex items-center gap-1 text-base font-medium text-[--text-dark-primary] transition-colors"
            aria-label="Volver a la página anterior"
          >
            <i className="ri-arrow-left-line text-[20px] text-[--base-fill-4]" />
            <span>{backTitle || 'Back'}</span>
          </button>
          <AbakoLogo width={32} />
          <span></span>
        </div>
      )}

      {/* 2 & 3. Header + Tabs */}
      {(headerContent || (tabs && tabs.length > 0)) && (
        <header className="headerApp flex flex-col w-full shrink-0">
          {headerContent && <div className="headerContent w-full shrink-0">{headerContent}</div>}

          {tabs && tabs.length > 0 && (
            <div className="w-full shrink-0">
              <TabsLine tabs={tabs} activeTab={activeTab!} onTabChange={onTabChange!} />
            </div>
          )}
        </header>
      )}

      {/* 4. Contenido de la página */}
      <main className="flex-1 w-full overflow-y-auto">
        <div className={contentClassName ?? 'pageContent'}>{children}</div>
      </main>
    </div>
  );
}
